import { type Dispatch, type SetStateAction, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@clerk/clerk-react';
import useCreateEventFlowMutation from '@/state/mutations/flows/use-create-event-flow.ts';
import TimePicker from '@/components/common/time-picker';
import { addDays, format, parse } from 'date-fns';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { useSearchParams } from 'react-router';

const addEventFlowSchema = z
  .object({
    title: z.string(),
    scheduleNow: z.boolean(),
    eventDayId: z.string().optional(),
    eventSlotId: z.string().optional(), // TODO: slots
    startTime: z.string().optional(),
    endTime: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.scheduleNow) {
      if (!data.eventDayId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Day is required',
          path: ['eventDayId'],
        });
      }
      if (!data.startTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start time is required',
          path: ['startTime'],
        });
      }
    }
  });

type AddEventFlowSchema = z.infer<typeof addEventFlowSchema>;

const FALLBACK_START_TIME = '08:00';

const AddFlowModal = ({ open, setOpen }: { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { userId } = useAuth();
  const isWideScreen = useBreakpointUp('md');

  const event = useEventStore((s) => s.event);
  const { mutateAsync: createEventFlowMutation } = useCreateEventFlowMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<AddEventFlowSchema>({
    resolver: zodResolver(addEventFlowSchema),
    defaultValues: {
      title: undefined,
      scheduleNow: true,
      eventDayId: undefined,
      eventSlotId: undefined,
      startTime: FALLBACK_START_TIME,
      endTime: undefined,
    },
  });

  useEffect(() => {
    if (event?.days?.[0]?.id) {
      const day = event?.days?.[0];
      reset({
        title: undefined,
        scheduleNow: true,
        eventDayId: day.id,
        eventSlotId: undefined,
        startTime: minuteOffsetToTime(day.startOffsetMinutes),
        endTime: undefined,
      });
    }
  }, [event?.days, reset]);

  const onSubmit = async (values: AddEventFlowSchema) => {
    if (!event?.id || !userId) {
      notification.notifyError(t('event.detail.addFlow.notifications.addFlowFailed'));
      return;
    }
    try {
      const { endTime, scheduleNow, ...restValues } = values;
      const res = await createEventFlowMutation({
        eventId: event.id,
        flow: {
          ...restValues,
          ...(scheduleNow
            ? {
                // Only include endTime if it has a value
                ...(endTime ? { endTime } : {}),
              }
            : {
                eventDayId: undefined,
                eventSlotId: undefined,
                startTime: undefined,
                endTime: undefined,
              }),
        },
      });
      notification.notifySuccess(t('event.detail.addFlow.notifications.addFlowSuccess'));
      if (isWideScreen) {
        searchParams.set('key', res.id);
        setSearchParams(searchParams);
      }
      setOpen(false);
      reset();
    } catch {
      notification.notifyError(t('event.detail.addFlow.notifications.addFlowFailed'));
    }
  };

  const flowDay = useWatch({ control, name: 'eventDayId' });
  const scheduleNow = useWatch({ control, name: 'scheduleNow', defaultValue: true });
  useEffect(() => {
    if (flowDay && scheduleNow) {
      const day = event?.days.find((day) => day.id === flowDay);
      if (day) {
        setValue('startTime', minuteOffsetToTime(day.startOffsetMinutes));
        setValue('endTime', undefined);
      }
    }
  }, [flowDay, scheduleNow, event?.days, setValue]);

  return (
    <Modal
      open={open}
      data-testid="add-contributor-modal"
      handleClose={() => setOpen(false)}
      title={t('event.detail.addFlow.addTitle')}
      description={t('event.detail.addFlow.addDescription')}
      sx={{ maxWidth: '350px' }}
      contentSx={{ pt: 1 }}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" color="info" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            data-testid="add-edit-contributor-button"
            variant="contained"
            color="info"
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.save')}
          </Button>
        </Stack>
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={2} sx={{ width: '100%' }}>
        <Controller
          name="title"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              required
              type="text"
              variant="standard"
              label={t('event.detail.addFlow.values.title')}
              helperText={fieldState.error ? fieldState.error.message : null}
              error={!!fieldState.error}
            />
          )}
        />
        <Controller
          name="scheduleNow"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(_, checked) => field.onChange(checked)} />}
              label={t('event.detail.addFlow.values.scheduleNow', undefined, 'Schedule now')}
            />
          )}
        />
        <Controller
          name="eventDayId"
          control={control}
          render={({ field }) => (
            <Box>
              <FormControl fullWidth variant="standard" error={!!errors.eventDayId}>
                <InputLabel>{`${t('event.detail.addFlow.values.eventDay.label')}${scheduleNow ? ' *' : ''}`}</InputLabel>
                <Select {...field} data-testid="day-select" disabled={!scheduleNow}>
                  <MenuItem value="" disabled>
                    {t('event.detail.addFlow.values.eventDay.description')}
                  </MenuItem>
                  {event?.days.map((day, i) => (
                    <MenuItem key={`day-${i}`} value={day.id}>
                      {t('event.detail.addFlow.values.eventDay.value', {
                        dayId: `${i + 1}`,
                        date: format(addDays(event.startDate, i), 'dd/MM/yyyy'),
                      })}
                    </MenuItem>
                  ))}
                </Select>
                {errors.eventDayId && (
                  <Typography variant="caption" color="error">
                    {errors.eventDayId.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        />
        <Stack direction="row" spacing={1} alignItems="flex-start" width="100%">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name="startTime"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TimePicker
                  label={`${t('event.detail.addFlow.values.startTime')}${scheduleNow ? ' *' : ''}`}
                  name="startTime"
                  views={['hours', 'minutes']}
                  disabled={!scheduleNow}
                  value={value ? parse(value, 'HH:mm', new Date()) : null}
                  onChange={(value) => {
                    if (value) {
                      onChange(format(value, 'HH:mm'));
                    } else {
                      onChange(null);
                    }
                  }}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      required: scheduleNow,
                      disabled: !scheduleNow,
                      error: !!errors.startTime,
                      helperText: errors.startTime?.message,
                      fullWidth: true,
                      sx: { minWidth: 0 },
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name="endTime"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TimePicker
                  label={t('event.detail.addFlow.values.endTime')}
                  name="endTime"
                  views={['hours', 'minutes']}
                  disabled={!scheduleNow}
                  value={value ? parse(value, 'HH:mm', new Date()) : null}
                  onChange={(value) => {
                    if (value) {
                      onChange(format(value, 'HH:mm'));
                    } else {
                      onChange(undefined);
                    }
                  }}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      required: false,
                      disabled: !scheduleNow,
                      error: !!errors.endTime,
                      helperText: errors.endTime?.message,
                      fullWidth: true,
                      sx: { minWidth: 0 },
                      placeholder: 'flexible',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddFlowModal;
